// client/src/views/Interactive_parts/POILayer.js
import React, { useState, useCallback, useEffect } from 'react';
import POIMarker from '../../components/POIMarker';

function POILayer({
  pois,
  foundPOIs,
  showFound,
  customCategories,
  customTypes = [],
  hoveredPoiId,
  hoveredType,
  setHoveredPoiId,
  visibleTypes,
  visibleCategories,
  showAllPoiLabels,
  typeColours,
  onCategoryUpdate,
  isMapOwnerOrFounder,
  mapId,
  user,
  mapOwnerId,
  onToggleFound,
  selectedPoiId,
  setSelectedPoiId,
  hideSidebarHover,
  onOpenEditModal = () => {},
  onCloseEditModal = () => {},
  onOpenInfoModal = () => {},
  onCloseInfoModal = () => {},
  overlayOptions = [],
  activeOverlayId = null,
}) {
  const [openModalPoiId, setOpenModalPoiId] = useState(null);
  const [modalEditingPoiId, setModalEditingPoiId] = useState(null);
  const foundPOIsSafe = Array.isArray(foundPOIs) ? foundPOIs : [];
  const [livePois, setLivePois] = useState(pois);

  useEffect(() => {
    setLivePois(pois);
  }, [pois]);

  useEffect(() => {
    const onSaved = (e) => {
      const detail = e?.detail || {};
      const poiId = detail?.poiId ?? detail?.id ?? null;
      if (poiId == null) return;

      const lat = Number(detail?.lat ?? detail?.y);
      const lng = Number(detail?.lng ?? detail?.x);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      setLivePois((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        let changed = false;

        const next = arr.map((p) => {
          if (!p) return p;
          const pid = p.id ?? p.poi_id ?? p.poiId ?? p.ID ?? null;
          if (pid == null) return p;
          if (String(pid) !== String(poiId)) return p;

          changed = true;
          return {
            ...p,
            x: lng,
            y: lat,
          };
        });

        return changed ? next : prev;
      });
    };

    try {
      window.addEventListener('pa-poi-position-saved', onSaved);
    } catch {}

    return () => {
      try {
        window.removeEventListener('pa-poi-position-saved', onSaved);
      } catch {}
    };
  }, []);

  const inSet = (set, value) => {
    if (value == null) return false;
    return set.has(value) || set.has(String(value));
  };

  const canCurrentUserEdit = !!isMapOwnerOrFounder;

  const filteredPOIs = livePois.filter((poi) => {
    if (!poi) return false;
    const catId = poi.category_id;
    const catName = poi.category;
    const typeId = poi.type_id;
    const typeName = poi.type;

    const hasCategory = catId != null || !!catName;
    const hasType = typeId != null || !!typeName;

    const catPass = inSet(visibleCategories, catName) || inSet(visibleCategories, catId);
    const typePass = inSet(visibleTypes, typeName) || inSet(visibleTypes, typeId);

    return hasCategory && hasType && catPass && typePass;
  });

  const handleOpenModal = useCallback(
    (poiId) => {
      setOpenModalPoiId(poiId);
      onOpenInfoModal();
    },
    [onOpenInfoModal]
  );

  const handleCloseModal = useCallback(() => {
    setOpenModalPoiId(null);
    setModalEditingPoiId(null);
    onCloseInfoModal();
    onCloseEditModal();
  }, [onCloseInfoModal, onCloseEditModal]);

  const handleStartEdit = useCallback(() => {
    setModalEditingPoiId(openModalPoiId);
    onOpenEditModal();
  }, [openModalPoiId, onOpenEditModal]);

  const handleStopEdit = useCallback(() => {
    setModalEditingPoiId(null);
    onCloseEditModal();
  }, [onCloseEditModal]);

  const resolveTypeColourFromMap = (poi, typeObj) => {
    if (!typeColours || typeof typeColours !== 'object') return '';

    const candidates = [];

    if (poi.type != null) candidates.push(poi.type);
    if (poi.type_id != null) candidates.push(poi.type_id);
    if (typeObj && typeObj.id != null) candidates.push(typeObj.id);
    if (typeObj && typeObj.name) candidates.push(typeObj.name);

    for (const rawKey of candidates) {
      const key = String(rawKey);
      if (Object.prototype.hasOwnProperty.call(typeColours, key)) {
        return typeColours[key];
      }
    }

    return '';
  };

  return (
    <>
      {filteredPOIs.map((poi) => {
        const isFound = foundPOIsSafe.includes(poi.id);
        const isHiddenFound = isFound && !showFound;
        const opacityValue = isHiddenFound ? 0.3 : 1;

        const shouldFlash =
          hoveredPoiId === poi.id ||
          ((hoveredType != null &&
            (hoveredType === (poi.type ?? poi.type_id) ||
              String(hoveredType) === String(poi.type_id) ||
              String(hoveredType) === String(poi.type)) &&
            hoveredPoiId == null));

        const showThisLabel = showAllPoiLabels || (!hideSidebarHover && hoveredPoiId === poi.id);

        const categoryObj =
          (Array.isArray(customCategories) &&
            customCategories.find((c) => String(c.id ?? c) === String(poi.category_id ?? poi.category))) ||
          {};
        const typeObj =
          (Array.isArray(customTypes) &&
            customTypes.find((t) => String(t.id ?? t) === String(poi.type_id ?? poi.type))) ||
          {};

        const typeColourFromMap = resolveTypeColourFromMap(poi, typeObj);

        const resolvedColour = poi.colour || poi.color || typeObj.color || typeColourFromMap || '#ff0000';

        const resolvedShape = typeObj.shape || poi.shape || 'circle';

        const poiProps = {
          key: poi.id,
          poi,
          customCategories,
          customTypes,
          shouldFlash,
          mapId,
          userId: user?.id,
          mapOwnerId,
          typeColours,
          onCategoryUpdate,
          canEdit: canCurrentUserEdit,
          showLabel: showThisLabel,
          onMouseEnter: () => setHoveredPoiId(poi.id),
          onMouseLeave: () => setHoveredPoiId(null),
          found: isFound,
          opacity: opacityValue,
          labelOpacity: opacityValue,
          iconOpacity: opacityValue,
          onToggleFound: () => onToggleFound(poi.id),
          user,
          selectedPoiId,
          setSelectedPoiId,
          hideSidebarHover,

          poiId: poi.id,
          poiName: poi.poi_name || poi.name || '',
          x: poi.x,
          y: poi.y,
          typeId: poi.type_id ?? poi.type,
          typeName: typeObj.name || poi.type_name || '',
          typeColour: resolvedColour,
          typeShape: resolvedShape,
          categoryId: poi.category_id ?? poi.category,
          categoryName: categoryObj.name || poi.category_name || '',
          colour: resolvedColour,
          shape: resolvedShape,
          description: poi.description || '',
          iconFile: poi.icon_file || '',
          imageUrl: poi.image_url || '',
          labelBorder: typeof poi.label_border !== 'undefined' ? poi.label_border : 1,
          labelBgColour: poi.label_bg_colour || '#000000',
          map_id: poi.map_id,

          overlayOptions,
          activeOverlayId,

          onOpenModal: () => handleOpenModal(poi.id),
          onCloseModal: handleCloseModal,
          onStartEdit: canCurrentUserEdit ? handleStartEdit : undefined,
          onStopEdit: canCurrentUserEdit ? handleStopEdit : undefined,
          modalOpen: openModalPoiId === poi.id,
          isEditing: modalEditingPoiId === poi.id,
        };

        return <POIMarker {...poiProps} />;
      })}
    </>
  );
}

export default POILayer;
